import type { Dispatch, SetStateAction } from "react";
import { useCallback } from "react";

import closeDarkIcon from "../../../assets/img/icons/closeDarkIcon.svg";
import searchDarkIcon from "../../../assets/img/icons/searchDarkIcon.svg";
import SearchInput from "../../../components/SearchInput/SearchInput";
import { useSiteData } from "../../../contexts/SiteDataContext";
import { useLocalized } from "../../../i18n/localized";
import type { RatingCategory } from "../../../types";

interface TabSwitchProps {
  ratingList: RatingCategory[];
  setActiveId?: (id: number) => void;
  activeTabId: number | string | null;
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
}

function TabSwitch({
  ratingList,
  setActiveId = () => {},
  activeTabId,
  searchQuery,
  setSearchQuery,
}: TabSwitchProps) {
  const { siteData, setSiteData } = useSiteData();
  const text = useLocalized();

  const toggleSearch = () => {
    const newIsSearchOpen = !siteData.isSearchOpen;
    setSiteData({ ...siteData, isSearchOpen: newIsSearchOpen });

    if (!newIsSearchOpen && searchQuery) {
      setSearchQuery("");
    }
  };

  const handleTabClick = useCallback(
    (itemId: number) => {
      setActiveId(itemId);
    },
    [setActiveId],
  );

  return (
    <div className="flex md:gap-12 gap-5 font-bold text-dark tab-switcher">
      {ratingList.map((rating, index) => (
        <button
          key={index}
          type="button"
          className={`pt-2 pb-[0.375rem] md:pt-3 md:pb-[0.5rem] whitespace-nowrap cursor-pointer text-sm md:text-base leading-6 md:rounded-b-[2px] ${
            rating.order === activeTabId
              ? "active border-yellow border-b-2 md:border-b-4"
              : "border-transparent border-b-2 md:border-b-4"
          }`}
          onClick={() => {
            handleTabClick(rating.order);
          }}
        >
          {text(rating, "nav_name") || text(rating, "name")}
        </button>
      ))}

      <div className="rounded-lg bg-light px-3 py-2 flex-shrink-0 hidden md:flex flex-1">
        <div
          className="cursor-pointer w-12 h-10 items-center justify-center flex"
          onClick={toggleSearch}
        >
          <img
            src={siteData.isSearchOpen ? closeDarkIcon : searchDarkIcon}
            alt={siteData.isSearchOpen ? "Close" : "Search"}
            className={`select-none ${
              siteData.isSearchOpen ? "w-[0.79rem]" : "w-[1.125rem]"
            }`}
          />
        </div>

        {siteData.isSearchOpen ? (
          <SearchInput value={searchQuery} onChange={setSearchQuery} />
        ) : null}
      </div>
    </div>
  );
}

export default TabSwitch;
